import { apiClient } from '@/shared/api/http-client';

import type {
    AddClientContactInput,
    AttachClientContactInput,
    ClientListFilters,
    CreateClientInput,
    CreateClientOutput,
    GetClientByIdOutput,
    GetClientContactCandidatesOutput,
    GetClientContactsOutput,
    GetClientCountOutput,
    GetClientListOutput,
    GetClientPersonOutput,
    GetClientProfileOutput,
    GetClientRelationshipsOutput,
    GetContactProfileOutput,
    UpdateClientProfileInput,
    UpdateContactProfileInput,
} from '../model/client.types';

export const clientsApi = {
    attachContact: async (
        clientId: string,
        data: AttachClientContactInput,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.post(`/clients/${clientId}/contacts/attach`, data, signal);
    },

    addContact: async (
        clientId: string,
        data: AddClientContactInput,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.post(`/clients/${clientId}/contacts`, data, signal);
    },

    addPracticeMember: async (id: string, signal?: AbortSignal): Promise<void> => {
        return apiClient.post(`/clients/${id}/practice-members`, undefined, signal);
    },

    create: async (data: CreateClientInput, signal?: AbortSignal): Promise<CreateClientOutput> => {
        return apiClient.post('/clients', data, signal);
    },

    getById: async (id: string, signal?: AbortSignal): Promise<GetClientByIdOutput> => {
        return apiClient.get(`/clients/${id}`, signal);
    },

    getContactCandidates: async (
        clientId: string,
        filters: { search: string; page_size?: number },
        signal?: AbortSignal,
    ): Promise<GetClientContactCandidatesOutput> => {
        const params = new URLSearchParams({ search: filters.search });
        if (filters.page_size) params.set('page_size', filters.page_size.toString());

        return apiClient.get(`/clients/${clientId}/contact-candidates?${params}`, signal);
    },

    getContacts: async (id: string, signal?: AbortSignal): Promise<GetClientContactsOutput> => {
        return apiClient.get(`/clients/${id}/contacts`, signal);
    },

    getContactProfile: async (
        personId: string,
        signal?: AbortSignal,
    ): Promise<GetContactProfileOutput> => {
        return apiClient.get(`/contacts/${personId}/profile`, signal);
    },

    getCount: async (signal?: AbortSignal): Promise<GetClientCountOutput> => {
        return apiClient.get('/clients/count', signal);
    },

    getPerson: async (id: string, signal?: AbortSignal): Promise<GetClientPersonOutput> => {
        return apiClient.get(`/persons/${id}`, signal);
    },

    getProfile: async (id: string, signal?: AbortSignal): Promise<GetClientProfileOutput> => {
        return apiClient.get(`/clients/${id}/profile`, signal);
    },

    getRelationships: async (
        id: string,
        signal?: AbortSignal,
    ): Promise<GetClientRelationshipsOutput> => {
        return apiClient.get(`/clients/${id}/relationships`, signal);
    },

    list: async (
        filters: ClientListFilters = {},
        signal?: AbortSignal,
    ): Promise<GetClientListOutput> => {
        const params = new URLSearchParams();

        if (filters.search) params.set('search', filters.search);
        if (filters.page_size) params.set('page_size', filters.page_size.toString());
        if (filters.cursor) params.set('cursor', filters.cursor);

        const queryString = params.toString();
        const endpoint = `/clients${queryString ? `?${queryString}` : ''}`;

        return apiClient.get(endpoint, signal);
    },

    updateContactProfile: async (
        data: UpdateContactProfileInput,
        signal?: AbortSignal,
    ): Promise<void> => {
        return apiClient.put(`/contacts/${data.id}/profile`, data, signal);
    },

    updateProfile: async (data: UpdateClientProfileInput, signal?: AbortSignal): Promise<void> => {
        return apiClient.put(`/clients/${data.id}/profile`, data, signal);
    },
};
